import { EditEmail } from '@Components/Mailing';
import { useSendEmail } from '@Hooks/Mailing';
import { useNotifyFailedRequest } from '@Hooks/useNotifyFailedRequest';
import { ApiSendEmailRequest } from '@Model';
import { Button, isOk, Modal, useToasts, useStateFromProps } from '@synqo/frontend-component-library';
import { Dispatch, FC, SetStateAction, useCallback } from 'react';

interface EmailPreviewModalProperties {
    isPreparing: boolean;
    emailRequest: ApiSendEmailRequest | undefined;
    setEmailRequest: Dispatch<SetStateAction<ApiSendEmailRequest | undefined>>;
    onEmailSent?: () => Promise<void>;
}

export const EmailPreviewModal: FC<EmailPreviewModalProperties> = ({
    isPreparing,
    emailRequest,
    setEmailRequest,
    onEmailSent,
}) => {
    const { pushToast } = useToasts();
    const { sendEmail, isPending } = useSendEmail();
    const { notifyFailedRequest } = useNotifyFailedRequest({
        title: 'Email-Versand fehlgeschlagen',
        message: 'Email-Versand war nicht erfolgreich.',
    });

    // The draft is edited locally so typing never re-renders the hosting frame; it is re-seeded
    // whenever the frame prepares a new email (a new emailRequest identity).
    const [draft, setDraft] = useStateFromProps(emailRequest);

    const close = useCallback(() => setEmailRequest(undefined), [setEmailRequest]);

    const onSend = useCallback(async () => {
        if (draft === undefined) return;
        const sendResult = await sendEmail(draft);
        if (isOk(sendResult)) {
            close();
            pushToast({
                title: 'Erfolgreicher Email-Versand',
                message: 'Die Email wurde versendet.',
                variant: 'success',
            });
            if (onEmailSent !== undefined) {
                await onEmailSent();
            }
        } else {
            notifyFailedRequest(sendResult);
        }
    }, [draft, sendEmail, close, pushToast, notifyFailedRequest, onEmailSent]);

    if (isPreparing) {
        return (
            <Modal isOpen={true} onClose={close} title="Email-Bearbeitung">
                <p>Email wird vorbereitet...</p>
            </Modal>
        );
    }

    if (draft === undefined) {
        return null;
    }

    return (
        <Modal
            isOpen={true}
            onClose={close}
            title="Email-Bearbeitung"
            footer={
                <Button appearance="filled" onClick={onSend} locked={isPending}>
                    Versenden
                </Button>
            }
        >
            <EditEmail
                sendEmailRequest={draft}
                setSendEmailRequest={setDraft as Dispatch<SetStateAction<ApiSendEmailRequest>>}
            />
        </Modal>
    );
};
